import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  PredictorModelState,
  SelectedStudyAreaModel,
} from 'src/app/state/predictor-model/predictor-model.state';
import { TerrainDataService } from '../../services/terrain-data.service';
import { CanvasService } from '../../services/canvas.service';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { TerrainLoadingStatus } from '../../enums/terrain-loading-status';
import { SimulationService } from '../../services/simulation.service';
import { TerrainRenderingStatus } from '../../enums/terrain-rendering-status';
import { Severity } from 'src/app/global/enums/severity';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { InteractionType } from '../../enums/interaction-type';
import { SimulationState } from '../../enums/simulation-state';
import { StatusMessage } from '../../interfaces/status-message';
import { SimulationLoopService } from '../../services/simulation-loop.service';
import { LicenseTermsConditionModalComponent } from '../../components/license-terms-condition-modal/license-terms-condition-modal.component';
import { DialogService } from 'primeng/dynamicdialog';
import { UserActions } from 'src/app/state/user/user.actions';
import { UserState } from 'src/app/state/user/user.state';

@Component({
  selector: 'app-predictor-page',
  templateUrl: './predictor-page.component.html',
  styleUrls: ['./predictor-page.component.scss'],
})
export class PredictorPageComponent implements OnDestroy, AfterViewInit {
  ngUnsubscribe: Subject<void> = new Subject<void>();
  @Select(PredictorModelState.showDetails) showDetails$!: Observable<boolean>;
  @Select(PredictorModelState.showControls) showControls$!: Observable<boolean>;
  @Select(PredictorModelState.showCanvas) showCanvas$!: Observable<boolean>;

  @Select(PredictorModelState.terrainLoadingStatus)
  terrainLoadingStatus$!: Observable<TerrainLoadingStatus>;

  @Select(PredictorModelState.studyAreaStatusMessage)
  statusMessage$!: Observable<StatusMessage>;

  @Select(PredictorModelState.selectedStudyAreaData)
  selectedStudyAreaData$!: Observable<SelectedStudyAreaModel>;

  @Select(PredictorModelState.interactionType)
  interactionType$!: Observable<InteractionType>;

  showPaintInfoModal: boolean = false;
  terrainRenderedStatus = TerrainRenderingStatus.Rendered;


  @ViewChild('canvas')
  private canvasRef!: ElementRef;
  private get canvas(): HTMLCanvasElement {
    return this.canvasRef.nativeElement;
  }
  private isInteracting: boolean = false;

  /* column width */
  width: string = '25rem';
  /* default sidebar visibility */
  visibleSidebar = true;

  simulationState: typeof SimulationState = SimulationState;

  constructor(
    private terrainDataService: TerrainDataService,
    private canvasService: CanvasService,
    private store: Store,
    public simulationService: SimulationService,
    private readonly simulationLoopService: SimulationLoopService,
    private readonly dialogService: DialogService
  ) { }

  toggleSidebar() {
    this.visibleSidebar = !this.visibleSidebar;
    this.canvasService.onWindowResize(this.visibleSidebar);
  }

  initializeCanvas() {
    this.canvasService.configureWithoutTerrain(this.canvas);
    this.canvasService.animate();
    this.canvasService.onWindowResize(this.visibleSidebar);

    this.canvas.addEventListener('pointerdown', (event: MouseEvent) => {
      this.isInteracting = true;
      this.canvasService.onPointerDown(event);
    });
    this.canvas.addEventListener('pointermove', (event: MouseEvent) => {
      if (this.isInteracting) {
        this.canvasService.onPointerMove(event);
      }
    });
    this.canvas.addEventListener('pointerup', (event) => {
      this.isInteracting = false;
      this.canvasService.onPointerUp(event);
    });
    this.canvas.addEventListener('click', (event: MouseEvent) => {
      if (this.store.selectSnapshot(PredictorModelState.showDetails)) {
        this.canvasService.updateDetails(event);
      }
    });
    this.canvas.addEventListener('wheel', (event: WheelEvent) => {
      this.canvasService.handleScrollWheel(event);
    });
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  ngAfterViewInit(): void {
    this.initializeCanvas();
    this.openTermsAndConditionsModal();

    // we have selected a study area and its shape
    this.selectedStudyAreaData$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((studyArea: SelectedStudyAreaModel) => {
        if (studyArea && studyArea.shape) {
          // show the canvas
          this.store.dispatch(
            new PredictorModelActions.SetCanvasVisibility(true)
          );

          this.canvasService.stopCurrentProcess();

          this.canvasService.updateStudyAreaOnCanvas(studyArea.shape);

          // get and display the terrain. Since we do not store in state, we need to fetch from here. This is the way
          if (!studyArea.simulationId) return;
          if (!studyArea.terrainId) return;

          // if we are uploading, we need to wait for the upload to finish before we can render the terrain
          if (
            studyArea.status &&
            studyArea.status !== TerrainLoadingStatus.Done
          )
            return;

          this.store.dispatch(
            new PredictorModelActions.SetTerrainState(
              studyArea.simulationId,
              TerrainRenderingStatus.Loading
            )
          );

          this.terrainDataService.loadData(studyArea.simulationId).subscribe({
            next: () => {
              this.terrainDataService
                .clearTerrain(studyArea.simulationId!)
                .subscribe({
                  next: () => {
                    this.canvasService.eraseAllAgentsFromCanvas();
                  },
                  error: (error) => {
                    console.error(error);
                  },
                });
              this.simulationService.reset(studyArea.simulationId!);
            },
          });
        }
      });

    this.store.dispatch(new PredictorModelActions.GetStudyAreas());

    this.terrainLoadingStatus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (status) => {
        this._notifyUserTerrainStatusChanged(status);
        // so we need to update this some so that we stop building if we are fetching something else.
        if (status !== TerrainLoadingStatus.Done) {
          return;
        }

        this.canvasService.addTerrainToCanvas(
          this.terrainDataService.terrainGrid
        );
      },
    });

    this.interactionType$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((interactionType: InteractionType) => {
        this.showPaintInfoModal =
          interactionType === InteractionType.Paint ||
          interactionType === InteractionType.Drop;
      });
  }

  private _notifyUserTerrainStatusChanged(status: TerrainLoadingStatus) {
    let message: string = '';
    let severity: Severity = Severity.Success;
    switch (status) {
      case TerrainLoadingStatus.FailedOnCheckingStatus:
      case TerrainLoadingStatus.FailedOnDownload:
      case TerrainLoadingStatus.FailedOnUpload:
        severity = Severity.Error;
        message = 'Failed during processing DEM file.';
        break;
      case TerrainLoadingStatus.Done:
        message = 'DEM ready for simulation.';
        break;
      default:
        return;
    }
    this.store.dispatch(new NotificationsActions.Toast(message, severity));
  }

  onWindowResize() {
    this.canvasService.onWindowResize(this.visibleSidebar);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();

    this.simulationLoopService.killLastSimulationLoop()
    this.simulationService.changeSimulationState(SimulationState.Ready)
    this.simulationService.reset();
  }

  openTermsAndConditionsModal() {
    this.store.dispatch(new UserActions.GetTcsAcceptance()).subscribe(() => {
      const isTcAccepted: any = this.store.selectSnapshot(UserState.userTcAccepted);

      if (isTcAccepted === false || isTcAccepted === null) {
        this.dialogService.open(
          LicenseTermsConditionModalComponent,
          {
            width: '620px',
            dismissableMask: false,
            closable: false,
            closeOnEscape: false,
            styleClass: 'bottom-rounded basin-font-primary basin-display-flex basin-align-item-center centered-header-dialog',
            header: 'Digital Property License Agreement',
          }
        );
      }
    });
  }

}
